import type { ThemeName } from "../Hooks/useTheme";

/**
 * AntD-facing mirror of the CSS custom properties declared in
 * `styles/index.scss`.
 *
 * Why a mirror exists: SCSS cannot be imported into TS, and AntD's
 * ConfigProvider needs concrete values synchronously at render time — reading
 * them back with getComputedStyle would paint one frame with AntD's defaults.
 *
 * The two are kept honest by `assertTokensInSync()` below, which runs in dev
 * only and logs a warning if a value here drifts from the stylesheet.
 *
 * Source of truth for the values themselves: the "Awfar — Inbox" theme
 * (awfar-inbox.html), light and dark token blocks.
 */
export const themeTokens = {
  lightTheme: {
    "--bg-app": "#e4f1fd",
    "--bg-surface": "#ffffff",
    "--bg-surface-raised": "#ffffff",
    "--bg-surface-2": "#f1f7fe",
    "--bg-tint": "#eaf4ff",
    "--text-primary": "#0b2466",
    "--text-secondary": "#34508c",
    "--text-tertiary": "#4a659b",
    "--text-meta": "#8fa3c4",
    "--border-default": "#d6e6fa",
    "--border-subtle": "#e3edfa",
    "--border-strong": "#bcdbfd",
    "--danger-ink": "#c62a36",
    "--success-ink": "#15803d",
    "--warn-ink": "#c2410c",
    "--ds-primary": "#1a7ffd",
    "--ds-primary-soft": "#d6e9fe",
    "--ds-primary-ink": "#0b55c8",
    "--ds-ink-placeholder": "#5a75a8",
    "--ds-field-border": "#badbff",
    "--ds-field-border-focus": "#80bcff",
    "--ds-surface-selected": "#eaf4ff",
    "--ds-option-hover": "#f1f7fe",
    "--ds-line-table": "#c9dff8",
  },
  darkTheme: {
    "--bg-app": "#0a1120",
    "--bg-surface": "#141e30",
    "--bg-surface-raised": "#1b2740",
    "--bg-surface-2": "#1b2740",
    "--bg-tint": "#16304f",
    "--text-primary": "#e8eef9",
    "--text-secondary": "#afc0db",
    "--text-tertiary": "#8fa2c2",
    "--text-meta": "#929fb7",
    "--border-default": "#2d3b57",
    "--border-subtle": "#27344d",
    "--border-strong": "#35537f",
    "--danger-ink": "#ff8a93",
    "--success-ink": "#4fd38a",
    "--warn-ink": "#f2a765",
    "--ds-primary": "#4da6ff",
    "--ds-primary-soft": "#1d3a5e",
    "--ds-primary-ink": "#8cc6ff",
    "--ds-ink-placeholder": "#7b8eae",
    "--ds-field-border": "#3e5b88",
    "--ds-field-border-focus": "#5b93d6",
    "--ds-surface-selected": "#16304f",
    "--ds-option-hover": "#26385a",
    "--ds-line-table": "#2b3a55",
  },
} as const satisfies Record<ThemeName, Record<string, string>>;

/** Theme-invariant, declared once on :root in the stylesheet. */
export const brandTokens = {
  brand: "#1a7ffd",
  brandHover: "#0b63e5",
  brandLight: "#4da6ff",
  /** Controls: nav rows, icon buttons, toolbar controls (ds radius-lg). */
  radiusControl: 12,
  /** Form fields (ds radius-xl). */
  radiusField: 14,
  /** Field height (ds size-input). */
  fieldHeight: 42,
  /** Cards, tables, modals (ds radius-card). */
  radiusCard: 22,
  fontFamily:
    "'Poppins', 'IBM Plex Sans Arabic', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
} as const;

/** Chart series palette — identical in both themes. */
export const chartSeries = [
  "#1a7ffd",
  "#e0384a",
  "#8b5cf6",
  "#f5a524",
  "#1fbf55",
] as const;

/**
 * Dev-only guard against this file drifting from styles/index.scss.
 * Compares each mirrored token against its computed CSS value.
 */
export function assertTokensInSync(theme: ThemeName) {
  if (!import.meta.env.DEV) return;

  const computed = getComputedStyle(document.documentElement);
  const drift: string[] = [];

  for (const [name, expected] of Object.entries(themeTokens[theme])) {
    const actual = computed.getPropertyValue(name).trim();
    if (!actual) {
      drift.push(`${name} is not declared in the stylesheet`);
      continue;
    }
    if (normalize(actual) !== normalize(expected)) {
      drift.push(
        `${name}: stylesheet has "${actual}", tokens.ts has "${expected}"`
      );
    }
  }

  if (drift.length) {
    console.warn(
      `[tokens] styles/tokens.ts is out of sync with styles/index.scss for "${theme}":\n  ` +
        drift.join("\n  ")
    );
  }
}

const normalize = (value: string) => value.toLowerCase().replace(/\s+/g, "");
